/**
 * Audience routing for account notes — ported from synrgyinfinity's
 * VisibilityDropdown. A note can be directed to the Team (broker + admin) or
 * Admin only (all admins, including CBS internal admins), and read-time
 * visibility on the server matches accordingly.
 *
 * The legacy Internal (CBS-only) audience is no longer offered, but its meta is
 * retained so notes authored before its removal still render correctly.
 *
 * Brokers can only post to the Team, so the control collapses to a static
 * pill for them (`canChooseRestricted={false}`). Admins see both options as a
 * segmented toggle and default to Admin (private).
 */

import React from 'react';
import {Lock, Shield, Users} from 'lucide-react';
import {cn} from '@common-web/lib/utils';

export type NoteVisibility = 'TEAM' | 'ADMIN_ONLY' | 'INTERNAL';

interface VisibilityMeta {
  label: string;
  description: string;
  icon: typeof Users;
  /** Pill background + text classes. */
  pill: string;
  /** Selected segment fill. */
  segment: string;
}

export const NOTE_VISIBILITY_META: Record<NoteVisibility, VisibilityMeta> = {
  ADMIN_ONLY: {
    label: 'Admin (private)',
    description: 'Only admins — hidden from brokers',
    icon: Lock,
    pill: 'bg-indigo-100 text-indigo-700',
    segment: 'bg-indigo-100 text-indigo-800',
  },
  TEAM: {
    label: 'Team',
    description: 'Visible to brokers and admins on this account',
    icon: Users,
    pill: 'bg-emerald-100 text-emerald-700',
    segment: 'bg-emerald-100 text-emerald-800',
  },
  INTERNAL: {
    label: 'Internal',
    description: 'You and CBS internal only',
    icon: Shield,
    pill: 'bg-slate-100 text-slate-600',
    segment: 'bg-slate-100 text-slate-700',
  },
};

/** Composer default: private to admins unless the author can only post Team. */
export function defaultNoteVisibility(canChooseRestricted: boolean): NoteVisibility {
  return canChooseRestricted ? 'ADMIN_ONLY' : 'TEAM';
}

const RESTRICTED_OPTIONS: NoteVisibility[] = ['ADMIN_ONLY', 'TEAM'];

interface NoteVisibilitySelectProps {
  value: NoteVisibility;
  onChange: (value: NoteVisibility) => void;
  /** When false, only the Team audience is offered (brokers). */
  canChooseRestricted: boolean;
  disabled?: boolean;
}

export function NoteVisibilitySelect({
  value,
  onChange,
  canChooseRestricted,
  disabled,
}: NoteVisibilitySelectProps): React.ReactElement {
  const options = canChooseRestricted ? RESTRICTED_OPTIONS : (['TEAM'] as NoteVisibility[]);
  const selectedMeta = NOTE_VISIBILITY_META[value];

  // Nothing to pick between — render a static pill.
  if (options.length <= 1) {
    return <NoteVisibilityPill visibility={value} />;
  }

  return (
    <div className='flex min-w-0 flex-col gap-1'>
      <div
        role='radiogroup'
        aria-label='Note audience'
        className='inline-flex overflow-hidden rounded-md border border-border'
      >
        {options.map((option) => {
          const optMeta = NOTE_VISIBILITY_META[option];
          const OptIcon = optMeta.icon;
          const selected = option === value;
          return (
            <button
              key={option}
              type='button'
              role='radio'
              aria-checked={selected}
              disabled={disabled}
              onClick={() => onChange(option)}
              className={cn(
                'inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                selected
                  ? optMeta.segment
                  : 'bg-card text-muted-foreground hover:bg-muted',
              )}
            >
              <OptIcon className='h-3.5 w-3.5' />
              {optMeta.label}
            </button>
          );
        })}
      </div>
      <p className='text-[11px] leading-tight text-muted-foreground'>{selectedMeta.description}</p>
    </div>
  );
}

export function NoteVisibilityPill({visibility}: {visibility: NoteVisibility}): React.ReactElement {
  const meta = NOTE_VISIBILITY_META[visibility];
  const Icon = meta.icon;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-semibold whitespace-nowrap',
        meta.pill,
      )}
    >
      <Icon className='h-2.5 w-2.5' />
      {meta.label}
    </span>
  );
}
